import { Card } from '../../../molecules/Card/Card';
import { isEmpty, type ChartRow } from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { BarChart } from '../../../organisms/BarChart/BarChart';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type IntentBasedMentionsView = {
  /** `Fastest rising by mentions` — an em dash unless an intent genuinely rose. */
  kpis: KPIMetric[];
  /** One row per intent in the period, ranked by mentions; one `mentions` key. */
  rows: ChartRow[];
  xKey: string;
  series: ChartSeries[];
  /** Whether any mention is in scope. */
  hasRows: boolean;
  formatValue: (value: number) => string;
};

export type IntentBasedMentionsCardProps = WidgetBlockProps & {
  view?: IntentBasedMentionsView;
  /** Opens the conversations behind a bar (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * Figma `947:18142` — mentions grouped by what the customer wants.
 *
 * Every intent the period actually holds, ranked — no cap. The intents chip
 * offers fourteen and the default selection is three, so the usual reading is
 * a handful of bars and the full set stays legible against the 24px bar cap.
 *
 * Fastest rising is a two-period question: only a genuine rise can win it, so
 * with every intent flat or falling — or comparison off — it reads an em dash.
 */
export function IntentBasedMentionsCard({
  status,
  view,
  span = 12,
  chrome,
  onRetry,
  onViewDetails,
}: IntentBasedMentionsCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmpty(view.rows, view.series);
  return (
    <Card
      {...chrome}
      className={spanClass(span)}
      {...cardState(status, hasData, onRetry)}
      title="Intent-based mentions"
      helpText="Mentions grouped by what the customer wants — a complaint, a question, praise, a purchase — so care and marketing can see which intent is driving the volume."
      helpSupportingText="One measure, so one colour: the ranking is the chart. The figure above ranges over every intent in the period, including any the bars do not show."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <BarChart
            data={view.rows}
            xKey={view.xKey}
            onViewDetails={onViewDetails}
            series={view.series}
            orientation="horizontal"
            height={PLOT_HEIGHT}
            legend="none"
            formatValue={view.formatValue}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
